import { api } from '@/api';
import router from '@/router';
import { getLocalToken, removeLocalToken, saveLocalToken } from '@/utils';
import { AxiosError } from 'axios';
import { getStoreAccessors } from 'typesafe-vuex';
import { ActionContext } from 'vuex';
import { State } from '../state';
import {
    commitSetLessons,
    commitSetLesson, commitSetComments, commitSetBooks, commitSetBook, commitSetLessonsCategories,
} from './mutations';
import { CoursesState } from './state';
import { dispatchCheckApiError } from '../main/actions';

type MainContext = ActionContext<CoursesState, State>;

export const actions = {
    async actionGetLessonsCategories(context: MainContext) {
        try {
            const response = await api.getLessonsCategories(context.rootState.main.token);
            commitSetLessonsCategories(context, response.data);
        } catch (error) {
            await dispatchCheckApiError(context, error);
            commitSetLessonsCategories(context, []);
        }
    },
    async actionGetFreeLessons(context: MainContext) {
        try {
            const response = await api.getFreeLessons(context.rootState.main.token);
            return response.data;
        } catch (error) {
            await dispatchCheckApiError(context, error);
            return [];
        }
    },
    async actionGetLessons(context: MainContext, payload: any) {
        try {
            const response = await api.getLessons(context.rootState.main.token, payload);
            commitSetLessons(context, response.data);
        } catch (error) {
            await dispatchCheckApiError(context, error);
            commitSetLessons(context, []);
        }
    },
    async actionGetBooks(context: MainContext) {
        try {
            const response = await api.getBooks(context.rootState.main.token);
            commitSetBooks(context, response.data);
        } catch (error) {
            await dispatchCheckApiError(context, error);
            commitSetBooks(context, []);
        }
    },
    async actionGetBook(context: MainContext, payload: { id: string }) {
        try {
            const response = await api.getBook(context.rootState.main.token, payload.id);
            commitSetBook(context, response.data);
        } catch (error) {
            await dispatchCheckApiError(context, error);
            commitSetBook(context, null);
        }
    },
    async actionGetLesson(context: MainContext, payload: {lessonId: string}) {
        try {
            const response = await api.getLesson(context.rootState.main.token, payload.lessonId);
            commitSetLesson(context, response.data);
        } catch (error) {
            await dispatchCheckApiError(context, error);
            commitSetLesson(context, null);
        }
    },
    async actionSetViewLesson(context: MainContext, payload: {lessonId: string, time: number}) {
        try {
            const response = await api.setViewLesson(context.rootState.main.token, payload.lessonId, payload.time);
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionGetComments(context: MainContext) {
        try {
            const response = await api.getComments(context.rootState.main.token);
            if (response.data) {
                commitSetComments(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
            commitSetComments(context, []);
        }
    },
    async actionGetLessonComments(context: MainContext, payload: {lessonId: string}) {
        try {
            const response = await api.getLessonComments(context.rootState.main.token, payload.lessonId);
            if (response.data) {
                commitSetComments(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
            commitSetComments(context, []);
        }
    },
    async actionAddComment(context: MainContext, payload: {comment: string, lesson?: string}) {
        try {
            const response = await api.addComment(context.rootState.main.token, payload);
            if (payload.lesson) {
                await dispatchGetLessonComments(context, {lessonId: payload.lesson});
            } else {
                await dispatchGetComments(context);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionPurchaseBook(context: MainContext, payload: any) {
        try {
            const response = await api.submitPurchaseBook(context.rootState.main.token, payload);
            return response.data;
        } catch (error) {
            await dispatchCheckApiError(context, error);
            return null;
        }
    },
};

const { dispatch } = getStoreAccessors<CoursesState | any, State>('');

export const dispatchGetLessonsCategories = dispatch(actions.actionGetLessonsCategories);
export const dispatchGetFreeLessons = dispatch(actions.actionGetFreeLessons);
export const dispatchGetLessons = dispatch(actions.actionGetLessons);
export const dispatchGetBooks = dispatch(actions.actionGetBooks);
export const dispatchGetBook = dispatch(actions.actionGetBook);
export const dispatchGetLesson = dispatch(actions.actionGetLesson);
export const dispatchSetViewLesson = dispatch(actions.actionSetViewLesson);
export const dispatchGetComments = dispatch(actions.actionGetComments);
export const dispatchGetLessonComments = dispatch(actions.actionGetLessonComments);
export const dispatchAddComment = dispatch(actions.actionAddComment);
export const dispatchPurchaseBook = dispatch(actions.actionPurchaseBook);
